import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Input } from 'antd';
import { useState } from 'react';
import { isConsolidatorRecord } from './ClientRecordHeader';
import { useClientRecord } from './client-record-data';
import { EmptyValue, RecordCard } from './record-primitives';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/details/$id/clients')({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClientsTab />
        </PageController>
    );
}

type ClientQueryParams = RouterInputs['fleetClientDb']['listFleetClients'];
type ClientRow = NonNullable<RouterOutputs['fleetClientDb']['listFleetClients']>['rows'][number];
const columnHelper = createColumnHelper<ClientRow, NonNullable<ClientQueryParams['orderBy']>>();

const DETAILS_ROUTE = '/console/$orgId/configuration/fleet/client-database/details/$id';

/** The consolidator's clients: the same rows as the client database list, scoped to this parent. */
function ClientsTab() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { id } = Route.useParams();
    const { data: client } = useClientRecord(id);
    const [params, setParams] = useState<ClientQueryParams>({
        limit: 20,
        orderBy: { name: 'asc' },
        where: {},
    });
    const [searchText, setSearchText] = useState('');

    const isConsolidator = client ? isConsolidatorRecord(client) : false;
    const clientsQuery = trpc.fleetClientDb.listFleetClients.useQuery(
        isConsolidator ? { ...params, where: { ...params.where, parentId: id } } : skipToken,
    );

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: clientsQuery.data?.rows || [],
        total: clientsQuery.data?.total || 0,
        loading: clientsQuery.isLoading,
        queryParams: params,
        onParamsChange: setParams,
        columns: [
            columnHelper.accessor('clientNo', {
                id: 'clientNo',
                header: 'Client no',
                sortable: 'clientNo',
                width: 120,
                cell: ({ row }) => <span className="text-subtle-foreground tabular-nums">{row.clientNo}</span>,
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Client',
                sortable: 'name',
                cell: ({ row }) => <span className="font-semibold">{row.name}</span>,
            }),
            columnHelper.accessor('state', {
                id: 'state',
                header: 'State',
                sortable: 'state',
                width: 90,
                cell: ({ row }) => row.state || <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('vehiclesCount', {
                id: 'vehiclesCount',
                header: 'Vehicles',
                align: 'center',
                width: 90,
                cell: ({ row }) => <span className="tabular-nums">{row.vehiclesCount}</span>,
            }),
            columnHelper.accessor('driversCount', {
                id: 'driversCount',
                header: 'Drivers',
                align: 'center',
                width: 90,
                cell: ({ row }) => <span className="tabular-nums">{row.driversCount}</span>,
            }),
            columnHelper.accessor('accountManagerName', {
                id: 'accountManager',
                header: 'Account manager',
                cell: ({ row }) => row.accountManagerName || <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                width: 110,
                cell: ({ row }) => row.status || <EmptyValue>—</EmptyValue>,
            }),
            columnHelper.actions({
                width: 40,
                cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button.OpenInNewTab to={DETAILS_ROUTE} params={{ orgId, id: row.id }} />
                    </div>
                ),
            }),
        ],
    });

    if (client && !isConsolidator) {
        return null;
    }

    const count = client?.childrenCount ?? 0;

    return (
        <RecordCard padded={false}>
            <div className="flex flex-wrap items-center gap-2 border-separator border-b px-4 py-3">
                <Input.Search
                    placeholder="Search by name or client no"
                    allowClear
                    className="w-72"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    onSearch={(value) => {
                        const term = value.trim();
                        setParams((prev) => ({
                            ...prev,
                            offset: 0,
                            where: {
                                ...prev.where,
                                OR: term
                                    ? [
                                          { name: { contains: term, mode: 'insensitive' } },
                                          { clientNo: { contains: term, mode: 'insensitive' } },
                                      ]
                                    : undefined,
                            },
                        }));
                    }}
                />
                <span className="ml-auto text-subtle-foreground text-xs">
                    {count} {count === 1 ? 'client' : 'clients'}
                </span>
            </div>
            {clientsQuery.error ? (
                <div className="p-4">
                    <InlineQueryError compact error={clientsQuery.error} onRetry={() => void clientsQuery.refetch()} />
                </div>
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={({ id: childId }) => ({
                        className: 'cursor-pointer',
                        onClick(event) {
                            navigate({ to: DETAILS_ROUTE, params: { orgId, id: childId } }, event);
                        },
                    })}
                />
            )}
        </RecordCard>
    );
}
